import React from "react"
import { twMerge } from "tailwind-merge"

import AnimatedSection from "../../ProProfileShared/AnimatedSection"
import { getContrastingTextColor as getContrastingTextColorUtil } from "../utils/colorUtils"
import { getReviewDisplayData, renderStarRating } from "../utils/reviewUtils"
import { applyButtonTheme, getButtonClasses } from "../utils/themeUtils"
import { buildPublicPageUrl, buildServiceUrl, buildBookingUrl } from "../utils/urlUtils"
import { DEFAULT_HIGHLIGHT_CLASSES } from "../WebsiteBuilderV2Page"

import HeaderSectionMobile from "./HeaderSectionMobile"

// Helper function to determine text color based on background
const getContrastingTextColor = (layout, hasBackgroundImage, theme, isInWhiteContainer = false) => {
  // If content is in a white container (like scattered_images with multiple images), use dark text
  if (isInWhiteContainer) {
    return theme?.colors?.textDark || "#000000"
  }
  // If there's a background image or it's a full image layout, use light text
  if (hasBackgroundImage || layout === "full_image" || layout.includes("image")) {
    return theme?.colors?.textLight || "#FFFFFF"
  }
  // Otherwise use dark text for light backgrounds
  return theme?.colors?.textDark || "#55433F"
}

const HeroSectionMobile = ({ section, practice, theme, highlightedField }) => {
  const isHighlighted = highlightedField === `section-${section.id}`
  const settings = section.settings || {}
  const layout = settings.layout || "full_image"
  // const textAlign = settings.textAlign || "left"
  const overlayOpacity = settings.overlayOpacity || 30
  const showCTA = settings.showCTA !== undefined ? settings.showCTA : true
  const ctaText = settings.ctaText || "Book a Discover Call"
  const ctaAction = settings.ctaAction || "booking"
  const ctaTarget = settings.ctaTarget || ""

  // Get review data from practice
  const reviewData = getReviewDisplayData(practice, settings.showReviews)
  const showReviews = settings.showReviews !== undefined ? settings.showReviews : reviewData.showReviews

  const handleCTAClick = () => {
    const scrollToLayout = (layoutId) => {
      if (!layoutId) return
      const targetElement = document.querySelector(`[id^=section-${layoutId}-]`)
      if (targetElement) {
        targetElement.scrollIntoView({ behavior: "smooth" })
      }
    }
    switch (ctaAction) {
      case "page": {
        if (ctaTarget) {
          window.location.href = buildPublicPageUrl(ctaTarget, practice)
        }
        break
      }
      case "service":
        if (ctaTarget) {
          window.open(buildServiceUrl(ctaTarget, practice), "_blank")
        }
        break
      case "booking":
        window.open(buildBookingUrl(practice), "_blank")
        break
      case "contact":
        scrollToLayout("contact")
        break
      case "scroll":
        window.scrollBy({ top: window.innerHeight, behavior: "smooth" })
        break
      case "external":
        if (ctaTarget) {
          const url = ctaTarget.startsWith("http") ? ctaTarget : `https://${ctaTarget}`
          window.open(url, "_blank")
        }
        break
      default:
        break
    }
  }

  const renderContent = (hasBackgroundImage = false, customLayout = null, isInWhiteContainer = false) => {
    const currentLayout = customLayout || layout
    const textColor = getContrastingTextColor(currentLayout, hasBackgroundImage, theme, isInWhiteContainer)

    return (
      <div className="flex flex-col gap-4">
        {/* Text Content */}
        <div className="flex flex-col gap-2 md:gap-3">
          {section.headline && (
            <h1
              className="w-full"
              style={{
                fontSize: "36px",
                lineHeight: "40px",
                fontFamily: theme?.fonts?.heading || "Playfair Display, serif",
                fontWeight: theme?.fonts?.headingWeight || "700",
                color: settings.headlineColor || textColor,
                textAlign: settings.headingAlign || settings.textAlign || "center"
              }}>
              {section.headline}
            </h1>
          )}
          {section.subheadline && (
            <p
              className="w-full"
              style={{
                fontSize: settings.subheadlineSize || "16px",
                fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                fontWeight: theme?.fonts?.paragraphRegularWeight || "400",
                color: settings.subheadlineColor || textColor,
                textAlign: settings.subheadingAlign || settings.textAlign || "center"
              }}>
              {section.subheadline}
            </p>
          )}
        </div>

        {/* CTA Button and Reviews */}
        <div
          className="flex flex-col gap-3 md:gap-4"
          style={{
            alignItems:
              settings.ctaAlign === "center" ? "center" : settings.ctaAlign === "right" ? "flex-end" : "flex-start",
            marginTop: settings.ctaTopMargin ? `${settings.ctaTopMargin}px` : "48px"
          }}>
          {/* CTA Button */}
          {showCTA && (
            <button
              onClick={handleCTAClick}
              className={twMerge(
                "inline-flex items-center justify-center px-8 py-2.5 text-[14px] font-normal leading-[20px] text-white transition-all hover:shadow-lg md:px-5 md:py-1.5 md:text-base",
                getButtonClasses(theme?.buttonStyle)
              )}
              style={applyButtonTheme(
                {
                  backgroundColor: theme?.colors?.primary || "#BD654F",
                  color: getContrastingTextColorUtil(theme?.colors?.primary || "#BD654F", theme),
                  fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif"
                },
                theme
              )}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = theme?.colors?.primaryDark || "#A55A45"
                e.target.style.color = getContrastingTextColorUtil(theme?.colors?.primaryDark || "#A55A45", theme)
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = theme?.colors?.primary || "#BD654F"
                e.target.style.color = getContrastingTextColorUtil(theme?.colors?.primary || "#BD654F", theme)
              }}>
              {ctaText}
            </button>
          )}

          {/* Client Reviews */}
          {showReviews && (
            <div className="flex items-center">
              <div className="mr-2 flex h-[20px] items-start">
                {renderStarRating(reviewData.averageRating, "h-[20px] w-[20px]")}
              </div>
              <span
                className="text-[14px] font-semibold leading-[20px] underline md:text-base"
                style={{ color: settings.reviewTextColor || settings.subheadlineColor || textColor }}>
                {reviewData.reviewCount} verified review{reviewData.reviewCount !== 1 ? "s" : ""}
              </span>
            </div>
          )}
        </div>
      </div>
    )
  }

  // Layout-specific rendering based on Figma design
  switch (layout) {
    case "image_left":
    case "image_right":
      return (
        <section
          className={twMerge(
            "relative flex items-center justify-start",
            isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
          )}
          style={{ backgroundColor: theme?.colors?.bgLight || "#FFFFFF" }}>
          <AnimatedSection className="mx-auto w-full px-5 py-8">
            <div className="flex flex-col items-center gap-5">
              {/* Mobile Image */}
              {section.imageUrl || (settings.images && settings.images.length > 0) ? (
                <div className="relative w-full">
                  <div
                    className="h-[320px] w-full rounded-lg bg-cover bg-center"
                    style={{ backgroundImage: `url(${section.imageUrl || settings.images[0].url})` }}
                  />
                </div>
              ) : (
                <div className="relative w-full">
                  <div className="flex h-[320px] w-full items-center justify-center rounded-lg bg-gray-200">
                    <span className="text-gray-500">No image selected</span>
                  </div>
                </div>
              )}
              <div className="w-full flex-1">{renderContent(false)}</div>
            </div>
          </AnimatedSection>
        </section>
      )

    case "half_image_left":
    case "half_image_right":
      return (
        <section
          className={twMerge(
            "relative flex items-center justify-start",
            isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
          )}
          style={{ backgroundColor: theme?.colors?.bgLight || "#FFFFFF" }}>
          <AnimatedSection className="mx-auto w-full px-5 py-8">
            <div className="flex flex-col items-center gap-5">
              {/* Mobile Image */}
              {section.imageUrl || (settings.images && settings.images.length > 0) ? (
                <div className="relative w-full">
                  <div
                    className="h-[320px] w-full rounded-lg bg-cover bg-center"
                    style={{ backgroundImage: `url(${section.imageUrl || settings.images[0].url})` }}
                  />
                </div>
              ) : (
                <div className="relative w-full">
                  <div className="flex h-[320px] w-full items-center justify-center rounded-lg bg-gray-200">
                    <span className="text-gray-500">No image selected</span>
                  </div>
                </div>
              )}
              <div className="w-full flex-1">{renderContent(false)}</div>
            </div>
          </AnimatedSection>
        </section>
      )

    case "contained_image":
      return (
        <section
          className={twMerge(
            "relative flex min-h-[300px] items-center justify-center p-8 md:min-h-[700px]",
            isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
          )}
          style={{ backgroundColor: theme?.colors?.bgLight || "#FFFFFF" }}>
          {/* Image container with rounded corners */}
          <div className="absolute inset-8 overflow-hidden rounded-lg">
            {section.imageUrl && (
              <div
                className="absolute inset-0 bg-cover bg-center"
                style={{ backgroundImage: `url(${section.imageUrl})` }}
              />
            )}
            <div className="absolute inset-0" style={{ backgroundColor: `rgba(0, 0, 0, ${overlayOpacity / 100})` }} />
          </div>

          {/* Content overlaid on the image */}
          <AnimatedSection className="relative z-10 mx-auto max-w-4xl px-4 md:px-4">
            {renderContent(true, "contained_image")}
          </AnimatedSection>
        </section>
      )

    case "full_image":
    default:
      return (
        <section
          className={twMerge(
            "relative flex min-h-[300px] items-center justify-center md:min-h-[700px]",
            isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
          )}
          style={{ backgroundColor: theme?.colors?.primary || "#3B82F6" }}>
          {section.imageUrl && (
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url(${section.imageUrl})` }}
            />
          )}
          <div className="absolute inset-0" style={{ backgroundColor: `rgba(0, 0, 0, ${overlayOpacity / 100})` }} />

          {/* Header superpuesto para full_image */}
          <div className="absolute inset-x-0 top-0 z-30">
            <HeaderSectionMobile
              section={{ id: "hero-header", settings: { showPromoBanner: false } }}
              practice={practice}
              theme={theme}
              highlightedField={null}
              transparent={true}
            />
          </div>

          <AnimatedSection className="relative z-10 mx-auto max-w-4xl px-4 md:px-4">
            {renderContent(true, "full_image")}
          </AnimatedSection>
        </section>
      )
  }
}

export default HeroSectionMobile
